import React, {
  useCallback,
  useState,
  useEffect,
  ChangeEvent,
  FocusEvent,
  ReactNode,
} from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import { ColorNames } from '../../../constants/colorVars'
import { formFieldWrapper } from '../formFieldWrapper'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import styles from './styles.scss'

/**
 * NOTE: the compact size of this component is not driven off of theme, but instead it is
 * based on the width of the screen. This is because this component is often used in modals,
 * and we want it to be compact in smaller modals regardless of theme.
 **/

const cx = classnames.bind(styles)
const COMPACT_BREAKPOINT = 550 // this is a little bit arbitrary, but it works for going a little bigger on bigger screens (tablet)

export interface CheckboxButtonFieldProps {
  className?: string
  elmId?: string
  onChange?: (
    e: ChangeEvent<HTMLInputElement>,
    data: { value: string | string[]; checked: boolean }
  ) => void
  onBlur?: (e: FocusEvent) => void
  value?: string | string[]
  toggle?: boolean
  label?: ReactNode
  iconName?: string
  valueAsArray?: boolean
  compact?: boolean
  viewMode?: boolean
  checked?: boolean
  disabled?: boolean
  layoutDirection?: 'horizontal' | 'vertical'
  fluid?: boolean
}

const BaseCheckboxButtonField: React.FC<CheckboxButtonFieldProps> = ({
  className = '',
  elmId = 'base_checkbox_button_field',
  onChange,
  onBlur,
  compact: _compact = false,
  value,
  toggle,
  label,
  iconName,
  valueAsArray = false,
  checked = false,
  disabled = false,
  layoutDirection = 'vertical',
  fluid = false,
  ...rest
}) => {
  const { width } = useWindowDimensions()
  const [compact, setCompact] = useState(_compact || width < COMPACT_BREAKPOINT)

  useEffect(() => {
    setCompact(_compact || width < COMPACT_BREAKPOINT)
  }, [_compact, width])

  const newLabel = (
    <>
      {iconName ? (
        <Icon
          name={iconName}
          iconStyle='light'
          color='inherit'
          className={cx('icon', { compact })}
        />
      ) : (
        ' '
      )}
      <span className={cx('button-label', { compact })}>{label}</span>
    </>
  )

  const defaultButtonClassNames = [
    'checkbox-button',
    layoutDirection,
    className,
    compact ? 'compact' : '',
    fluid ? 'fluid' : '',
  ]

  const classNames = toggle ? [className] : defaultButtonClassNames

  const handleClick = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled || !onChange) return

      const newChecked = !checked
      let newValue = value

      if (valueAsArray && typeof value === 'string') {
        newValue = value.split(',')
      }

      const data = {
        value: newValue || '',
        checked: newChecked,
      }

      if (valueAsArray && typeof data.value === 'string') {
        data.value = data.value.split(',')
      }

      onChange(e, data)
    },
    [checked, disabled, onChange, value, valueAsArray]
  )

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLButtonElement>) => {
      if (onBlur) {
        onBlur(e)
      }
    },
    [onBlur]
  )

  const newValue = valueAsArray
    ? Array.isArray(value) && value.join(',')
    : value

  return (
    <Button
      role='secondary'
      isSelected={checked}
      color={checked ? ColorNames.Action : ColorNames.Gray}
      elmId={`${elmId}_checkbox_button`}
      aria-label={`${elmId}_checkbox_button`}
      aria-pressed={checked}
      aria-checked={checked}
      className={cx(...classNames)}
      onClick={handleClick}
      onBlur={handleBlur}
      disabled={disabled}
      value={newValue}
      size={compact ? 'small' : 'medium'}
      fluid={fluid}
      {..._.omit(
        rest,
        'size',
        'checkboxLabel',
        'showAnchor',
        'helpContent',
        'pendingApproval'
      )}
    >
      {newLabel}
    </Button>
  )
}

// @ts-ignore next line
const WrappedCheckboxButtonField = formFieldWrapper(fieldProps => (
  <BaseCheckboxButtonField {...fieldProps} />
))

const CheckboxButtonField: React.FC<CheckboxButtonFieldProps> = props => {
  return (
    <div
      className={cx({ 'even-spacing': props.layoutDirection === 'vertical' })}
    >
      <WrappedCheckboxButtonField {...props} />
    </div>
  )
}

export default CheckboxButtonField
